import { c as _c } from "react/compiler-runtime";
import { CurrencyEnum, LocaleEnum, Units } from '@systemeio/types';
import { centsToUnits, units } from '@systemeio/util';
import { ChartOptions } from 'chart.js';
import { ChartsSkeleton } from 'modules/main/components/chart-box-section/charts-skeleton';
import { MainChartEmptyState } from 'modules/main/components/chart-box-section/main-chart-empty-state';
import { MainChartTrend } from 'modules/main/components/chart-box-section/main-chart-trend';
import { RevenueCurrencyOption, RevenueCurrencySelect } from 'modules/main/components/chart-box-section/revenue-currency-select';
import { MAIN_CHART_AREA_CLASS_NAME } from 'modules/main/constants/main-chart';
import { useMainRevenue } from 'modules/main/hooks/use-main-revenue';
import { MainFilterInterface } from 'modules/main/types/main-filter-interface';
import { MainRevenueInterface, MainRevenueItemInterface } from 'modules/main/types/main-interface';
import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartCard } from 'shared/components/chart/chart-card';
import { lineChartOptions, lineChartPlugins, withTooltipTitles } from 'shared/components/chart/chart-config';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { toFullDate, toShortDate } from 'shared/utils/date-time-format';
const TOTAL_SERIES = 'total';
type RevenueSeriesKey = CurrencyEnum | typeof TOTAL_SERIES;
type RevenueWithSales = MainRevenueInterface & {
  total: MainRevenueItemInterface;
};
interface RevenueChartBoxProps {
  filter: MainFilterInterface;
}

/* A picked currency names itself before the value; the total and a lone currency show the value alone. */
export const getRevenueChartOptions = (currency: CurrencyEnum, locale?: LocaleEnum, tooltipTitles: string[] = [], currencyCode?: string): ChartOptions<'line'> => {
  const options = withTooltipTitles<'line'>(lineChartOptions, tooltipTitles);
  return {
    ...options,
    plugins: {
      ...options.plugins,
      tooltip: {
        ...options.plugins?.tooltip,
        callbacks: {
          ...options.plugins?.tooltip?.callbacks,
          ...(currencyCode && {
            beforeLabel: () => [currencyCode, '•']
          }),
          label: ({
            parsed
          }) => parsed.y === null ? '' : currencyFormat(units(parsed.y), currency, locale, true)
        }
      }
    }
  };
};

/*
 * With a single currency the chart is that currency, so its own trend is shown: the total trend
 * also counts currencies the previous period had and this one has not.
 */
const getSeriesKey = (revenue: RevenueWithSales, selected: RevenueSeriesKey) => {
  const [onlyItem] = revenue.items;
  if (revenue.items.length === 1 && onlyItem) return onlyItem.currency;
  return revenue.items.some(item => item.currency === selected) ? selected : TOTAL_SERIES;
};
const getSeries = (revenue: RevenueWithSales, key: RevenueSeriesKey) => revenue.items.find(item => item.currency === key) ?? revenue.total;
interface RevenueChartProps {
  onSelect: (key: RevenueSeriesKey) => void;
  revenue: RevenueWithSales;
  selected: RevenueSeriesKey;
}
const RevenueChart = t0 => {
  const $ = _c(69);
  const {
    onSelect,
    revenue,
    selected
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  let isSingleCurrency;
  let series;
  let seriesKey;
  let t1;
  let totalCaption;
  if ($[0] !== revenue || $[1] !== selected || $[2] !== t) {
    seriesKey = getSeriesKey(revenue, selected);
    series = getSeries(revenue, seriesKey);
    let t2;
    if ($[8] !== t) {
      t2 = t("dashboard.funnel.statistics.total");
      $[8] = t;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    totalCaption = t2;
    isSingleCurrency = revenue.items.length === 1;
    t1 = isSingleCurrency || seriesKey === TOTAL_SERIES ? undefined : series.currency.toUpperCase();
    $[0] = revenue;
    $[1] = selected;
    $[2] = t;
    $[3] = isSingleCurrency;
    $[4] = series;
    $[5] = seriesKey;
    $[6] = t1;
    $[7] = totalCaption;
  } else {
    isSingleCurrency = $[3];
    series = $[4];
    seriesKey = $[5];
    t1 = $[6];
    totalCaption = $[7];
  }
  const pickedCurrencyCode = t1;
  let t2;
  if ($[10] !== user?.dashboardLocale) {
    t2 = t3 => {
      const {
        currency,
        sum
      } = t3;
      return currencyFormat(Math.round(centsToUnits(sum)) as Units, currency, user?.dashboardLocale, true);
    };
    $[10] = user?.dashboardLocale;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const formatSum = t2;
  let t3;
  if ($[12] !== intlLocale || $[13] !== series.days) {
    let t4;
    if ($[15] !== intlLocale) {
      t4 = t5 => {
        const {
          date
        } = t5;
        return toShortDate(date, intlLocale);
      };
      $[15] = intlLocale;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t3 = series.days.map(t4);
    $[12] = intlLocale;
    $[13] = series.days;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[17] !== series.days) {
    t4 = series.days.map(_temp);
    $[17] = series.days;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t4) {
    t5 = [{
      data: t4
    }];
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t3 || $[22] !== t5) {
    t6 = {
      labels: t3,
      datasets: t5
    };
    $[21] = t3;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const data = t6;
  let t7;
  if ($[24] !== intlLocale || $[25] !== pickedCurrencyCode || $[26] !== series.currency || $[27] !== series.days || $[28] !== user?.dashboardLocale) {
    t7 = () => getRevenueChartOptions(series.currency, user?.dashboardLocale, series.days.map(t8 => {
      const {
        date: date_0
      } = t8;
      return toFullDate(date_0, intlLocale);
    }), pickedCurrencyCode);
    $[24] = intlLocale;
    $[25] = pickedCurrencyCode;
    $[26] = series.currency;
    $[27] = series.days;
    $[28] = user?.dashboardLocale;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  user?.dashboardLocale;
  let t8;
  if ($[30] !== t7) {
    t8 = t7();
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const options = t8;
  let t9;
  if ($[32] !== formatSum || $[33] !== revenue.total) {
    t9 = formatSum(revenue.total);
    $[32] = formatSum;
    $[33] = revenue.total;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t9 || $[36] !== totalCaption) {
    t10 = {
      value: TOTAL_SERIES,
      caption: totalCaption,
      amount: t9
    };
    $[35] = t9;
    $[36] = totalCaption;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== formatSum || $[39] !== revenue.items || $[40] !== t10) {
    let t12;
    if ($[42] !== formatSum) {
      t12 = item => ({
        value: item.currency,
        caption: item.currency.toUpperCase(),
        amount: formatSum(item)
      });
      $[42] = formatSum;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    t11 = [t10, ...revenue.items.map(t12)];
    $[38] = formatSum;
    $[39] = revenue.items;
    $[40] = t10;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const currencyOptions = t11;
  let t12;
  if ($[44] !== revenue.countDays || $[45] !== series.diffPercents) {
    t12 = <MainChartTrend countDays={revenue.countDays} diffPercents={series.diffPercents} />;
    $[44] = revenue.countDays;
    $[45] = series.diffPercents;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t) {
    t13 = t("dashboard.main.sales");
    $[47] = t;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t12 || $[50] !== t13) {
    t14 = <ChartCard.Title action={t12}>{t13}</ChartCard.Title>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== currencyOptions || $[53] !== formatSum || $[54] !== isSingleCurrency || $[55] !== onSelect || $[56] !== series || $[57] !== seriesKey || $[58] !== totalCaption) {
    t15 = isSingleCurrency ? <ChartCard.Value label={totalCaption}>{formatSum(series)}</ChartCard.Value> : <ChartCard.Control><RevenueCurrencySelect options={currencyOptions} value={seriesKey} onValueChange={onSelect} /></ChartCard.Control>;
    $[52] = currencyOptions;
    $[53] = formatSum;
    $[54] = isSingleCurrency;
    $[55] = onSelect;
    $[56] = series;
    $[57] = seriesKey;
    $[58] = totalCaption;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== t14 || $[61] !== t15) {
    t16 = <ChartCard.Header>{t14}{t15}</ChartCard.Header>;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] !== data || $[64] !== options) {
    t17 = <ChartCard.Body><div className={MAIN_CHART_AREA_CLASS_NAME}><Line data={data} options={options} plugins={lineChartPlugins} /></div></ChartCard.Body>;
    $[63] = data;
    $[64] = options;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== t16 || $[67] !== t17) {
    t18 = <ChartCard.Root>{t16}{t17}</ChartCard.Root>;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  return t18;
};
const hasSales = (revenue: MainRevenueInterface): revenue is RevenueWithSales => revenue.items.length > 0 && revenue.total.currency !== null;
export const RevenueChartBox = t0 => {
  const $ = _c(31);
  const {
    filter
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    intlLocale
  } = useUser();
  const {
    data: revenue,
    error
  } = useMainRevenue(filter);
  const [selected, setSelected] = useState(TOTAL_SERIES);
  if (error) {
    let t1;
    if ($[0] !== t) {
      t1 = t("dashboard.main.sales");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t) {
      t2 = t("core.error.internal_error_message");
      $[2] = t;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <MainChartEmptyState title={t2} />;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t1 || $[7] !== t3) {
      t4 = <ChartCard title={t1}>{t3}</ChartCard>;
      $[6] = t1;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (!revenue) {
    let t1;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ChartsSkeleton />;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    return t1;
  }
  if (!hasSales(revenue)) {
    let t1;
    if ($[10] !== t) {
      t1 = t("dashboard.main.sales");
      $[10] = t;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    let t2;
    if ($[12] !== intlLocale) {
      t2 = new Intl.NumberFormat(intlLocale).format(0);
      $[12] = intlLocale;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t) {
      t3 = t("dashboard.funnel.statistics.total");
      $[14] = t;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== t) {
      t4 = t("dashboard.main.chart.sales_empty_title");
      $[16] = t;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t) {
      t5 = t("dashboard.main.chart.empty_description");
      $[18] = t;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== t4 || $[21] !== t5) {
      t6 = <MainChartEmptyState title={t4} description={t5} />;
      $[20] = t4;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== t1 || $[24] !== t2 || $[25] !== t3 || $[26] !== t6) {
      t7 = <ChartCard title={t1} value={t2} valueLabel={t3}>{t6}</ChartCard>;
      $[23] = t1;
      $[24] = t2;
      $[25] = t3;
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    return t7;
  }
  let t1;
  if ($[28] !== revenue || $[29] !== selected) {
    t1 = <RevenueChart revenue={revenue} selected={selected} onSelect={setSelected} />;
    $[28] = revenue;
    $[29] = selected;
    $[30] = t1;
  } else {
    t1 = $[30];
  }
  return t1;
};
function _temp(t0) {
  const {
    value
  } = t0;
  return Math.round(centsToUnits(value));
}